import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Typography,
  CircularProgress,
  Box,
  Alert
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { downloadAmazonListings, downloadMyntraListings, downloadAjioListings, downloadNykaaListings, downloadFlipkartListings } from '../../services/marketplaceapi';
import apiClient from '../../services/api';

const DownloadListingsModal = ({ open, onClose, marketplace }) => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingCompanies, setFetchingCompanies] = useState(true);
  const [error, setError] = useState(null);

  // Fetch companies on component mount
  useEffect(() => {
    if (open) {
      fetchCompanies();
    }
  }, [open]);

  const fetchCompanies = async () => {
    setFetchingCompanies(true);
    setError(null);
    try {
      const response = await apiClient.get('/marketplace/companies/');
      setCompanies(response.data.results || response.data);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError('Failed to load companies. Please try again.');
      setCompanies([]);
    } finally {
      setFetchingCompanies(false);
    }
  };

  const handleCompanyChange = (event) => {
    setSelectedCompany(event.target.value);
  };

  const handleDownload = async () => {
    if (!selectedCompany) {
      setError('Please select a company first');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      // Call the appropriate download function based on marketplace
      if (marketplace === 'amazon') {
        response = await downloadAmazonListings(selectedCompany);
      } else if (marketplace === 'flipkart') {
        response = await downloadFlipkartListings(selectedCompany);
      } else if (marketplace === 'myntra') {
        response = await downloadMyntraListings(selectedCompany);
      } else if (marketplace === 'ajio') {
        response = await downloadAjioListings(selectedCompany);
      } else if (marketplace === 'nykaa') {
        response = await downloadNykaaListings(selectedCompany);
      } else {
        throw new Error(`Download not implemented for ${marketplace}`);
      }
      
      // Create a download link for the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename based on Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename;
      
      if (contentDisposition) {
        // Extract the filename from Content-Disposition header to respect server's extension
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        filename = filenameMatch ? filenameMatch[1] : getDefaultFilename(marketplace, selectedCompany);
      } else {
        filename = getDefaultFilename(marketplace, selectedCompany);
      }
      
      // Helper function to get the correct file extension based on marketplace
      function getDefaultFilename(marketplace, company) {
        // Use correct extension based on marketplace
        if (marketplace === 'myntra' || marketplace === 'flipkart') {
          return `${marketplace}_${company}.csv`;
        } else {
          // Amazon uses xls, Ajio and Nykaa use xlsx
          const extension = marketplace === 'amazon' ? 'xls' : 'xlsx';
          return `${marketplace}_${company}.${extension}`;
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Close modal after successful download
      onClose();
      
    } catch (err) {
      console.error(`Error downloading ${marketplace} listings:`, err);
      setError(`Failed to download listings: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={!loading ? onClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle>Download {marketplace.charAt(0).toUpperCase() + marketplace.slice(1)} Listings</DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Typography gutterBottom>
          Download listings for a specific company. Select a company from the dropdown below.
        </Typography>
        
        <FormControl fullWidth margin="normal">
          <InputLabel id="company-select-label">Company</InputLabel>
          <Select
            labelId="company-select-label"
            value={selectedCompany}
            onChange={handleCompanyChange}
            label="Company"
            disabled={fetchingCompanies || loading}
          >
            {fetchingCompanies ? (
              <MenuItem value="" disabled>
                Loading companies...
              </MenuItem>
            ) : companies.length === 0 ? (
              <MenuItem value="" disabled>
                No companies available
              </MenuItem>
            ) : (
              companies.map((company) => (
                <MenuItem key={company.id} value={company.name}>
                  {company.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose} 
          color="secondary" 
          disabled={loading}
        >
          Cancel
        </Button>
        
        <Button
          onClick={handleDownload}
          color="primary"
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
          disabled={!selectedCompany || loading}
        >
          {loading ? 'Downloading...' : 'Download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadListingsModal;
